// Note: this file is named `templatesComposable.ts` (not `templates.ts`)
// because `templates.ts` already exists as a legacy Options-API shim used by
// unit tests (`tests/unit/tests/stores/documents/templates.spec.js`). The two
// files are unrelated — the legacy shim is a self-contained snapshot for tests,
// while this composable is what the Pinia store actually uses at runtime.
import { type Ref, ref, computed } from "vue";

import { graphGet } from "@/helpers/api/apiGraph";
import DriveItem from "@/models/Documents/DriveItem";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";

import FileHelper from "@/helpers/utilities/FileHelper";
import TrackHelper from "@/helpers/utilities/TrackHelper";

import { useSettingsStore } from "@/stores/settings";
import { useInstitutionsStore } from "@/stores/institutions";

interface TemplateStructureGroup {
  label?: string;
  folders?: Record<string, { label: string }>;
}

interface TemplateItem {
  id: string;
  name: string;
  parentReference: { driveId: string; id?: string };
  folder?: boolean;
  specialItemType?: string;
  suffixIcon?: string;
  putInTemplateTypeSubfolder?: boolean;
  putInRootFolder?: boolean;
  documentNameFormat?: string;
  extension?: { color?: string; typeIcon?: string; suffixIcon?: string; [key: string]: unknown };
  newFolder?: boolean;
  [key: string]: unknown;
}

interface TemplateSpecialItem {
  driveId: string;
  itemId: string;
  specialItemType: string;
  suffixIcon?: string;
  putInTemplateTypeSubfolder?: boolean;
  putInRootFolder?: boolean;
  documentNameFormat?: string;
  color?: string;
  typeIcon?: string;
}

interface TemplateGroups {
  folders: TemplateItem[];
  specialFolders: TemplateItem[];
  files: TemplateItem[];
  specialFiles: TemplateItem[];
  newFolder: TemplateItem[];
}

interface TemplatesComposableDependencies {
  loadDriveItem: (driveId: string, itemId: string) => Promise<DriveItem>;
  // Lazy lookup so the composable can reference an action defined later in
  // the store setup (addSpecialItem still lives in the confidentialDocuments
  // section of index.ts).
  getAddSpecialItem: () => (
    confidentialId: string,
    driveId: string,
    itemId: string,
    specialItemType: string,
    persist?: boolean
  ) => Promise<unknown>;
}

export const useTemplatesComposable = (dependencies: TemplatesComposableDependencies) => {
  const templateMap: Ref<Record<string, unknown>> = ref({});
  const templateForFolderType: Ref<Record<string, Record<string, Record<string, unknown>>>> = ref({});
  const templatesForDriveItemMap: Ref<Record<string, unknown>> = ref({});

  const templatesForFolderType = computed(
    () => (folderSystem: string, folderIdentifier: string, templatesFolderItemId?: string) => {
      if (templateForFolderType.value[folderSystem] && templateForFolderType.value[folderSystem][folderIdentifier]) {
        return templateForFolderType.value[folderSystem][folderIdentifier][
          DriveItemHelper.getItemId(templatesFolderItemId)
        ];
      }
      return [];
    }
  );

  const loadTemplatesForFolderType = async (nctSystem: string, nctIdentifier: string) => {
    const settingsStore = useSettingsStore();
    const templateStructure = settingsStore.templateStructure;
    const templateGeneralRoot = settingsStore.templateFolders?.general;
    const templateInstitutionsRoot = settingsStore.templateFolders?.institutions;

    if (!templateStructure) {
      TrackHelper.registerError("Missing template setup");
      // eslint-disable-next-line no-console
      console.log("Missing Template Setup");
      return;
    }

    const templateFolderSteps: string[][] = [];
    if (nctSystem && templateStructure[nctSystem]) {
      if (Array.isArray(templateStructure[nctSystem])) {
        templateStructure[nctSystem].forEach((group: TemplateStructureGroup) => {
          if (Object.hasOwn(group, "folders")) {
            if (group.folders![nctIdentifier]) {
              const templatePathFromRoot: string[] = [];
              if (Object.hasOwn(group, "label")) {
                templatePathFromRoot.push(group.label!);
              }
              templatePathFromRoot.push(group.folders![nctIdentifier].label);
              templateFolderSteps.push(templatePathFromRoot);
            }
          }
        });
      } else {
        if (Object.hasOwn(templateStructure[nctSystem], "folders")) {
          if (templateStructure[nctSystem].folders[nctIdentifier]) {
            const templatePathFromRoot: string[] = [];
            if (Object.hasOwn(templateStructure[nctSystem], "label")) {
              templatePathFromRoot.push(...templateStructure[nctSystem].label.split("/"));
            }
            templatePathFromRoot.push(templateStructure[nctSystem].folders[nctIdentifier].label);
            templateFolderSteps.push(templatePathFromRoot);
          }
        } else if (Object.hasOwn(templateStructure[nctSystem], "label")) {
          templateFolderSteps.push([templateStructure[nctSystem].label]);
        }
      }
    }

    const templateItems: DriveItem[] = [];
    if (templateFolderSteps.length) {
      if (templateGeneralRoot && templateGeneralRoot.driveId !== "" && templateGeneralRoot.itemId !== "") {
        const generalTemplatesRootResponse = await graphGet(
          `/drives/${templateGeneralRoot.driveId}/items/${templateGeneralRoot.itemId}/?$expand=children${
            import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
          }`
        );
        const generalTemplatesRoot = new DriveItem(generalTemplatesRootResponse) as DriveItem & {
          children?: Array<{ id: string; name: string }>;
        };
        if (generalTemplatesRoot) {
          let actualTemplateFolder: (DriveItem & { children?: Array<{ id: string; name: string }> }) | null = null;
          for (let i = 0; i < templateFolderSteps.length; i++) {
            const templateFolderStep = templateFolderSteps[i];
            actualTemplateFolder = generalTemplatesRoot;
            for (let j = 0; j < templateFolderStep.length; j++) {
              const child = actualTemplateFolder?.children?.find(c => c.name === templateFolderStep[j]);
              if (child) {
                const actualTemplateFolderResponse = await graphGet(
                  `/drives/${templateGeneralRoot.driveId}/items/${child.id}/?$expand=children${
                    import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
                  }`
                );
                actualTemplateFolder = new DriveItem(actualTemplateFolderResponse) as DriveItem & {
                  children?: Array<{ id: string; name: string }>;
                };
              } else {
                actualTemplateFolder = null;
              }
            }
          }
          if (actualTemplateFolder) {
            let generalTemplates: DriveItem[] = [];
            if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
              const response = await graphGet(
                `/drives/${templateGeneralRoot.driveId}/items/${actualTemplateFolder.id}/?$expand=children`
              );
              generalTemplates = (response.children as Array<Record<string, unknown>>).map(
                child => new DriveItem(child)
              );
            } else {
              const response = await graphGet(
                `/drives/${templateGeneralRoot.driveId}/items/${actualTemplateFolder.id}/children?$expand=thumbnails`
              );
              if (response.value) {
                generalTemplates = (response.value as Array<Record<string, unknown>>).map(
                  child => new DriveItem(child)
                );
              } else {
                generalTemplates = (response as unknown as Array<Record<string, unknown>>).map(
                  child => new DriveItem(child)
                );
              }
            }
            templateItems.push(...generalTemplates);
          }
        }
      }

      if (
        templateInstitutionsRoot &&
        templateInstitutionsRoot.driveId !== "" &&
        templateInstitutionsRoot.itemId !== ""
      ) {
        const institutionTemplatesRootResponse = await graphGet(
          `/drives/${templateInstitutionsRoot.driveId}/items/${templateInstitutionsRoot.itemId}/?$expand=children${
            import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
          }`
        );

        type TemplateFolder = DriveItem & { id: string; children?: Array<{ id: string; name: string }> };
        const institutionTemplatesRoot = new DriveItem(institutionTemplatesRootResponse) as TemplateFolder;
        if (institutionTemplatesRoot) {
          const institutionStore = useInstitutionsStore();
          const activeInstitution = institutionStore.activeInstitution as { identifier?: string } | undefined;
          let activeInstitutionTemplateRoot: TemplateFolder | null =
            (institutionTemplatesRoot.children?.find(child =>
              child.name.startsWith(activeInstitution?.identifier ?? "")
            ) as TemplateFolder | undefined) ?? null;

          if (activeInstitutionTemplateRoot) {
            const activeInstitutionTemplateRootResponse = await graphGet(
              `/drives/${templateInstitutionsRoot.driveId}/items/${activeInstitutionTemplateRoot.id}/?$expand=children${
                import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
              }`
            );

            activeInstitutionTemplateRoot = new DriveItem(activeInstitutionTemplateRootResponse) as TemplateFolder;

            let actualTemplateFolder: TemplateFolder | null = null;
            for (let i = 0; i < templateFolderSteps.length; i++) {
              const templateFolderStep = templateFolderSteps[i];
              actualTemplateFolder = activeInstitutionTemplateRoot;
              for (let j = 0; j < templateFolderStep.length; j++) {
                const child = actualTemplateFolder?.children?.find(c => c.name === templateFolderStep[j]);
                if (child) {
                  const actualTemplateFolderResponse = await graphGet(
                    `/drives/${templateGeneralRoot!.driveId}/items/${child.id}/?$expand=children${
                      import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
                    }`
                  );
                  actualTemplateFolder = new DriveItem(actualTemplateFolderResponse) as TemplateFolder;
                } else {
                  actualTemplateFolder = null;
                  break;
                }
              }
            }

            if (actualTemplateFolder) {
              let institutionTemplates: DriveItem[] = [];
              if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
                const response = await graphGet(
                  `/drives/${templateGeneralRoot!.driveId}/items/${actualTemplateFolder.id}/?$expand=children`
                );
                institutionTemplates = (response.children as Array<Record<string, unknown>>).map(
                  child => new DriveItem(child)
                );
              } else {
                const response = await graphGet(
                  `/drives/${templateGeneralRoot!.driveId}/items/${actualTemplateFolder.id}/children?$expand=thumbnails`
                );
                institutionTemplates = (response.value as Array<Record<string, unknown>>).map(
                  child => new DriveItem(child)
                );
              }
              templateItems.push(...institutionTemplates);
            }
          }
        }
      }
    }
    return templateItems;
  };

  const handleTemplateItemsBeforePersist = (templateItems: TemplateItem[]) => {
    const settingsStore = useSettingsStore();
    const templates = FileHelper.addExtensionsToArrayOfFiles(templateItems) as TemplateItem[];

    if (settingsStore.templateSpecialItems?.length > 0) {
      templates.forEach(template => {
        const specialItem = settingsStore.templateSpecialItems.find(
          (candidate: TemplateSpecialItem) =>
            candidate.driveId === template.parentReference.driveId && candidate.itemId === template.id
        ) as TemplateSpecialItem | undefined;

        if (specialItem) {
          template.folder = true;
          template.specialItemType = specialItem.specialItemType;
          template.suffixIcon = specialItem.suffixIcon;
          template.putInTemplateTypeSubfolder = specialItem.putInTemplateTypeSubfolder;
          template.putInRootFolder = specialItem.putInRootFolder;
          template.documentNameFormat = specialItem.documentNameFormat;

          template.extension = JSON.parse(JSON.stringify(template.extension));
          if (template.extension) {
            template.extension.color = specialItem.color;
            template.extension.typeIcon = specialItem.typeIcon;
            template.extension.suffixIcon = specialItem.suffixIcon;
          }

          // Add to specialitemlist - call the action via injected dependency
          const addSpecialItem = dependencies.getAddSpecialItem();
          addSpecialItem("template", template.parentReference.driveId, template.id, specialItem.specialItemType, false);
        }
      });
    }

    const templateGroups: TemplateGroups = {
      folders: [],
      specialFolders: [],
      files: [],
      specialFiles: [],
      newFolder: []
    };
    templateGroups.folders = templates
      .filter(template => template.folder && !template.specialItemType)
      .sort((first, second) => first.name.localeCompare(second.name, settingsStore.language));
    templateGroups.specialFolders = templates
      .filter(template => template.folder && template.specialItemType)
      .sort((first, second) => first.name.localeCompare(second.name, settingsStore.language));
    templateGroups.files = templates
      .filter(template => !template.folder && !template.specialItemType)
      .sort((first, second) => first.name.localeCompare(second.name, settingsStore.language));
    templateGroups.specialFiles = templates
      .filter(template => !template.folder && template.specialItemType)
      .sort((first, second) => first.name.localeCompare(second.name, settingsStore.language));
    templateGroups.newFolder = [{ newFolder: true, extension: FileHelper.getClosedFolderTemplate() } as TemplateItem];
    return templateGroups;
  };

  const cloneTemplateForFolderType = (input: Record<string, Record<string, unknown>>) => {
    const clone: Record<string, Record<string, unknown>> = {};
    for (const key in input) {
      clone[key] = {};
      for (const innerKey in input[key]) {
        clone[key][innerKey] = input[key][innerKey];
      }
    }
    return clone;
  };

  const loadTemplates = async (
    destinationDriveId: string,
    destinationItemId: string,
    templateFolderDriveId: string,
    templateFolderItemId: string
  ) => {
    const calculatedDestinationItemId = DriveItemHelper.getItemId(destinationItemId);
    const destinationDriveItem = await dependencies.loadDriveItem(destinationDriveId, calculatedDestinationItemId);
    const calculatedTemplateFolderItemId = DriveItemHelper.getItemId(templateFolderItemId);

    if (
      templateForFolderType.value[destinationDriveItem.nctSystem] &&
      templateForFolderType.value[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier] &&
      templateForFolderType.value[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
        calculatedTemplateFolderItemId
      ]
    ) {
      return templateForFolderType.value[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
        calculatedTemplateFolderItemId
      ];
    }

    if (!templateFolderItemId) {
      const nctSystem = String(destinationDriveItem.nctSystem);
      const nctIdentifier = String(destinationDriveItem.nctIdentifier);
      let templateFolderItems = await loadTemplatesForFolderType(nctSystem, nctIdentifier);
      if (!templateFolderItems || templateFolderItems.length === 0) {
        templateFolderItems = await loadTemplatesForFolderType("other", "other");
      }
      const persistedTemplateFolderItems = handleTemplateItemsBeforePersist(templateFolderItems as TemplateItem[]);

      const stateClone = cloneTemplateForFolderType(templateForFolderType.value) || {};
      if (!stateClone[nctSystem]) {
        stateClone[nctSystem] = {};
      }
      if (!(stateClone[nctSystem] as Record<string, unknown>)[nctIdentifier]) {
        (stateClone[nctSystem] as Record<string, unknown>)[nctIdentifier] = {};
      }
      ((stateClone[nctSystem] as Record<string, Record<string, unknown>>)[nctIdentifier] as Record<string, unknown>)[
        calculatedTemplateFolderItemId
      ] = persistedTemplateFolderItems;
      templateForFolderType.value = stateClone as Record<string, Record<string, Record<string, unknown>>>;
    } else {
      if (
        templateForFolderType.value[destinationDriveItem.nctSystem] &&
        templateForFolderType.value[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier] &&
        templateForFolderType.value[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
          templateFolderItemId
        ]
      ) {
        return templateForFolderType.value[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
          templateFolderItemId
        ];
      }

      let templateItems: DriveItem[];
      if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
        const response = await graphGet(
          `/drives/${templateFolderDriveId}/items/${templateFolderItemId}/?$expand=children`
        );
        templateItems = response.children.map((child: unknown) => new DriveItem(child));
      } else {
        const response = await graphGet(
          `/drives/${templateFolderDriveId}/items/${templateFolderItemId}/children?$expand=thumbnails`
        );
        templateItems = response?.value.map((child: unknown) => new DriveItem(child)) || [];
      }

      const templateFolderItems = handleTemplateItemsBeforePersist(
        templateItems.map((child: DriveItem) => new DriveItem(child)) as unknown as TemplateItem[]
      );

      const stateClone = cloneTemplateForFolderType(templateForFolderType.value) || {};
      if (!stateClone[destinationDriveItem.nctSystem]) {
        stateClone[destinationDriveItem.nctSystem] = {};
      }
      if (
        !(stateClone[destinationDriveItem.nctSystem] as Record<string, unknown>)[destinationDriveItem.nctIdentifier]
      ) {
        (stateClone[destinationDriveItem.nctSystem] as Record<string, unknown>)[destinationDriveItem.nctIdentifier] =
          {};
      }
      (
        (stateClone[destinationDriveItem.nctSystem] as Record<string, Record<string, unknown>>)[
          destinationDriveItem.nctIdentifier
        ] as Record<string, unknown>
      )[templateFolderItemId] = templateFolderItems;
      templateForFolderType.value = stateClone as Record<string, Record<string, Record<string, unknown>>>;
    }
  };

  const addTemplatesToState = (
    destinationDriveId: string,
    destinationItemId: string,
    templatesFolderDriveId: string | undefined,
    templatesFolderItemId: string | undefined,
    templates: unknown
  ) => {
    const calculatedDestinationItemId = DriveItemHelper.getItemId(destinationItemId);
    const stateDriveId = templatesFolderDriveId || "root";
    const stateItemId = DriveItemHelper.getItemId(templatesFolderItemId);

    const templateMapClone = JSON.parse(JSON.stringify(templateMap.value));
    if (!templateMapClone[destinationDriveId]) {
      templateMapClone[destinationDriveId] = {};
    }
    if (!templateMapClone[destinationDriveId][calculatedDestinationItemId]) {
      templateMapClone[destinationDriveId][calculatedDestinationItemId] = {};
    }
    if (!templateMapClone[destinationDriveId][calculatedDestinationItemId][stateDriveId]) {
      templateMapClone[destinationDriveId][calculatedDestinationItemId][stateDriveId] = {};
    }
    templateMapClone[destinationDriveId][calculatedDestinationItemId][stateDriveId][stateItemId] = templates;
    templateMap.value = templateMapClone;
  };

  return {
    templateMap,
    templateForFolderType,
    templatesForDriveItemMap,
    templatesForFolderType,
    loadTemplates,
    addTemplatesToState
  };
};
